// Admin Orders list. Status filter (via ?status=) + free-text search across
// order id, customer email, guest email. Click a row to drill into the
// OrderDetail page.
import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useAuth } from '@workos-inc/authkit-react'
import { apiFetch } from '../../lib/api'
import { StatusBadge } from './Overview'

interface OrderRow {
  id: string
  number: number
  status: string
  totalCents: number
  currency: string
  createdAt: string
  customerName: string | null
  customerEmail: string | null
  itemCount: number
}
interface OrdersResponse { orders: OrderRow[]; total: number; limit: number; offset: number; statuses: string[] }

const STATUS_LABELS: Record<string, string> = {
  pending_payment: 'Pending payment',
  paid:            'Paid',
  in_production:   'In production',
  shipped:         'Shipped',
  delivered:       'Delivered',
  cancelled:       'Cancelled',
  refunded:        'Refunded',
}

export default function AdminOrders() {
  const { getAccessToken } = useAuth()
  const [params, setParams] = useSearchParams()
  const status = params.get('status') ?? ''
  const search = params.get('search') ?? ''
  const page   = Math.max(1, parseInt(params.get('page') ?? '1') || 1)
  const PAGE_SIZE = 50
  const offset = (page - 1) * PAGE_SIZE
  const [data, setData] = useState<OrdersResponse | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [searchInput, setSearchInput] = useState(search)

  useEffect(() => { setSearchInput(search) }, [search])

  // Debounce search input → URL param so we don't spam the API.
  useEffect(() => {
    const t = setTimeout(() => {
      if (searchInput === search) return
      const next = new URLSearchParams(params)
      if (searchInput) next.set('search', searchInput); else next.delete('search')
      setParams(next, { replace: true })
    }, 350)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput])

  useEffect(() => {
    (async () => {
      try {
        const qs = new URLSearchParams()
        if (status) qs.set('status', status)
        if (search) qs.set('search', search)
        qs.set('limit',  String(PAGE_SIZE))
        qs.set('offset', String(offset))
        const d = await apiFetch<OrdersResponse>(getAccessToken as unknown as () => Promise<string | undefined>, `/api/admin/orders?${qs.toString()}`)
        setData(d); setErr(null)
      } catch (e) { setErr((e as Error).message) }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, search, page])

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1
  function goToPage(n: number) {
    const next = new URLSearchParams(params)
    if (n <= 1) next.delete('page'); else next.set('page', String(n))
    setParams(next, { replace: true })
  }
  // Snap back to page 1 whenever the filter changes — keeps "no results on
  // page 5" from being a confusing dead state.
  useEffect(() => {
    if (page > 1 && data && data.total <= offset) goToPage(1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, status, search])

  const setStatus = (next: string) => {
    const p = new URLSearchParams(params)
    if (next) p.set('status', next); else p.delete('status')
    setParams(p, { replace: true })
  }

  const tabs = useMemo(() => ([{ key: '', label: 'All' }, ...(data?.statuses ?? []).map((s) => ({ key: s, label: STATUS_LABELS[s] ?? s }))]), [data])

  return (
    <div className="p-8">
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <div className="eyebrow">Processing</div>
          <h1 className="font-heading uppercase font-bold text-3xl text-ink">Orders</h1>
        </div>
        <input
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search order #, email…"
          className="input max-w-xs"
        />
      </div>

      <div className="flex flex-wrap gap-1.5 mb-5 border-b border-border pb-1">
        {tabs.map((t) => (
          <button key={t.key} onClick={() => setStatus(t.key)}
            className={'px-3 py-1.5 text-xs font-semibold uppercase tracking-wide rounded transition-colors ' +
              (status === t.key ? 'bg-gold/15 text-gold-dark' : 'text-ink-3 hover:bg-surface')}>
            {t.label}
          </button>
        ))}
      </div>

      {err && <div className="card text-red-700 mb-4">{err}</div>}
      {!data && !err && <div className="text-ink-3 text-sm">Loading…</div>}

      {data && (
        <>
          <div className="card !p-0 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-surface text-[10px] uppercase tracking-wider text-ink-3">
                <tr>
                  <th className="text-left px-4 py-2 font-bold">Order</th>
                  <th className="text-left px-4 py-2 font-bold">Customer</th>
                  <th className="text-left px-4 py-2 font-bold">Status</th>
                  <th className="text-right px-4 py-2 font-bold">Items</th>
                  <th className="text-right px-4 py-2 font-bold">Total</th>
                  <th className="text-right px-4 py-2 font-bold">Placed</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.orders.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-10 text-center text-ink-3 text-sm">No orders match.</td></tr>
                )}
                {data.orders.map((o) => (
                  <tr key={o.id} className="hover:bg-surface transition-colors">
                    <td className="px-4 py-3">
                      <Link to={`/admin/orders/${o.id}`} className="font-mono text-xs text-ink-2 hover:text-gold-dark">#{o.number}</Link>
                    </td>
                    <td className="px-4 py-3">
                      <Link to={`/admin/orders/${o.id}`} className="block hover:text-gold-dark group">
                        <div className="text-ink group-hover:text-gold-dark truncate max-w-[260px]">{o.customerName ?? '—'}</div>
                        <div className="text-xs text-ink-3 group-hover:text-gold-dark truncate max-w-[260px]">{o.customerEmail ?? '—'}</div>
                      </Link>
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={o.status} /></td>
                    <td className="px-4 py-3 text-right text-ink-2">{o.itemCount}</td>
                    <td className="px-4 py-3 text-right font-semibold text-ink">${(o.totalCents / 100).toFixed(2)}</td>
                    <td className="px-4 py-3 text-right text-xs text-ink-3">{new Date(o.createdAt).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-xs text-ink-3">
              Showing <strong className="text-ink-2">{data.orders.length === 0 ? 0 : offset + 1}–{offset + data.orders.length}</strong> of <strong className="text-ink-2">{data.total}</strong> order{data.total === 1 ? '' : 's'}
            </div>
            {totalPages > 1 && (
              <div className="flex items-center gap-1">
                <button disabled={page <= 1} onClick={() => goToPage(page - 1)}
                  className="px-2 py-1 text-xs border border-border rounded text-ink-2 hover:border-gold hover:text-ink disabled:opacity-40 disabled:cursor-not-allowed">‹ Prev</button>
                <span className="px-3 text-xs text-ink-3">Page {page} of {totalPages}</span>
                <button disabled={page >= totalPages} onClick={() => goToPage(page + 1)}
                  className="px-2 py-1 text-xs border border-border rounded text-ink-2 hover:border-gold hover:text-ink disabled:opacity-40 disabled:cursor-not-allowed">Next ›</button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
